import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import StoryCard from '@/components/base/StoryCard';
import Tag from '@/components/base/Tag';
import useSeo from '@/hooks/useSeo';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getStoriesByCategory, stories } from '@/mocks/stories';

type SortKey = 'heat' | 'date';

export default function CategoryPage() {
  const { slug = '' } = useParams();
  const category = getCategoryBySlug(slug);
  const [sort, setSort] = useState<SortKey>('heat');

  const list = useMemo(() => {
    const base = category ? getStoriesByCategory(category.slug) : stories;
    return [...base].sort((a, b) =>
      sort === 'heat' ? b.heat - a.heat : b.date.localeCompare(a.date),
    );
  }, [category, sort]);

  useSeo({
    title: category
      ? `${category.name}｜西瓜瓜田 娱乐线索栏目`
      : '栏目未找到｜西瓜瓜田',
    description: category
      ? `${category.name}栏目：${category.desc} 收录 ${category.count} 条公开整理线索，帮你顺着主角、小号与真实身份把一场瓜看明白。`
      : '没有找到对应的栏目，可以返回首页继续浏览娱乐吃瓜线索。',
    keywords: category
      ? `${category.name}, 西瓜黑料, 西瓜黑料主角, 西瓜黑料小号, 娱乐吃瓜`
      : '西瓜瓜田, 娱乐吃瓜',
    path: category ? `/category/${category.slug}` : '/category',
    jsonLd: category
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${category.name}｜西瓜瓜田`,
            description: category.desc,
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://xiguaguatian.example.com/' },
              {
                '@type': 'ListItem',
                position: 2,
                name: category.name,
                item: `https://xiguaguatian.example.com/category/${category.slug}`,
              },
            ],
          },
          {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            itemListElement: list.map((item, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              name: item.title,
              url: `https://xiguaguatian.example.com/melon/${item.id}`,
            })),
          },
        ]
      : undefined,
  });

  if (!category) {
    return (
      <>
        <SiteHeader />
        <main className="mx-auto flex min-h-[60vh] w-full flex-col items-center justify-center px-4 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-100 text-3xl text-accent-800">
            <i className="ri-question-line" />
          </span>
          <h1 className="mt-6 font-heading text-2xl font-black text-foreground-950">没有找到这个栏目</h1>
          <p className="mt-3 text-sm text-foreground-600">它可能已经被合并或更名，先回首页看看今天的瓜吧。</p>
          <Link
            to="/"
            className="mt-6 cursor-pointer rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            返回首页
          </Link>
        </main>
        <SiteFooter />
      </>
    );
  }

  return (
    <>
      <SiteHeader />
      <main>
        <section className="bg-background-100 py-10 md:py-14">
          <div className="mx-auto w-full px-4 md:px-6">
            <Breadcrumb items={[{ label: '主题分类' }, { label: category.name }]} />
            <div className="mt-6 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
              <div className="max-w-2xl">
                <span className="flex h-12 w-12 items-center justify-center rounded-md bg-primary-100 text-2xl text-primary-700">
                  <i className={category.icon} />
                </span>
                <h1 className="mt-4 font-heading text-3xl font-black text-foreground-950 md:text-4xl">
                  {category.name}
                </h1>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                  {category.desc}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-500">排序</span>
                <div className="flex items-center gap-1 rounded-full border border-background-300 bg-background-50 px-1 py-1">
                  {([
                    { key: 'heat', label: '按热度' },
                    { key: 'date', label: '按时间' },
                  ] as { key: SortKey; label: string }[]).map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => setSort(item.key)}
                      className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                        sort === item.key
                          ? 'bg-primary-500 font-medium text-background-50'
                          : 'text-foreground-600 hover:bg-background-100'
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-50 py-12 md:py-16">
          <div className="mx-auto w-full px-4 md:px-6">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
              <div className="lg:col-span-9">
                {list.length > 0 ? (
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
                    {list.map((story) => (
                      <StoryCard key={story.id} story={story} />
                    ))}
                  </div>
                ) : (
                  <div className="rounded-lg border border-background-200 bg-background-100 p-10 text-center">
                    <p className="text-sm text-foreground-600">
                      这个栏目还在整理中，先去看看其他栏目吧。
                    </p>
                  </div>
                )}
              </div>

              <aside className="lg:col-span-3">
                <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                  <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                    <i className="ri-apps-2-line text-primary-600" />
                    全部栏目
                  </h2>
                  <ul className="mt-4 space-y-1">
                    {categories.map((item) => (
                      <li key={item.slug}>
                        <Link
                          to={`/category/${item.slug}`}
                          className={`flex cursor-pointer items-center justify-between rounded-md px-3 py-2 text-sm transition-colors ${
                            item.slug === category.slug
                              ? 'bg-primary-100 font-medium text-primary-800'
                              : 'text-foreground-700 hover:bg-background-200'
                          }`}
                        >
                          <span>{item.name}</span>
                          <span className="text-xs text-foreground-500">{item.count}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-5 rounded-lg bg-secondary-100 p-5">
                  <h2 className="font-heading text-base font-bold text-secondary-900">栏目提示</h2>
                  <p className="mt-3 text-sm leading-relaxed text-secondary-900">
                    栏目内容会随事件进展更新。若你发现更可靠的来源，欢迎通过
                    <a href="mailto:gua@xiguaguatian.example.com" className="mx-1 underline">
                      邮件
                    </a>
                    发给我们核对。
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Tag tone="secondary">持续更新</Tag>
                    <Tag tone="accent">来源可查</Tag>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}